/**
 * フォーマットヘルパー
 */
import { DOW_LABELS } from '@/lib/calendar-day';

export const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '−';
  return `¥${Number(value).toLocaleString('ja-JP')}`;
};

export const formatPercent = (value: number | null | undefined, digits = 1): string => {
  if (value == null || !Number.isFinite(value)) return '−';
  return `${(value * 100).toFixed(digits)}%`;
};

export const formatCount = (value: number | null | undefined, unit = ''): string => {
  if (value == null) return '−';
  return `${Number(value).toLocaleString('ja-JP')}${unit}`;
};

/** 目標との乖離。`¥-400,000` ではなく `−¥400,000` にして符号をマイナス記号として読めるようにする
 *  (元は KpiTargetProgressPage.tsx にのみ存在。v0.5⑤ でDashboardOverviewPage.tsxも使うため共通化)。 */
export const formatVariance = (v: number | null | undefined): string => {
  if (v == null) return '−';
  if (v === 0) return formatCurrency(0);
  return v > 0 ? `+${formatCurrency(v)}` : `−${formatCurrency(Math.abs(v))}`;
};

export const formatMonthLabel = (yyyyMmDd: string): string => {
  const [y, m] = yyyyMmDd.split('-');
  return `${y}年${Number(m)}月`;
};

/** v0.4 F-1: 'YYYY-MM-DD' 形式の日付を日本語表示に変換する (シフト関連画面で共有)。
 *  タイムゾーン変換を避けるため、文字列の日付部分をそのまま暦日として解釈する
 *  (deadline_at 等の ISO 日時から呼ぶ場合も `.slice(0, 10)` で日付部分だけ渡すこと)。 */
export const formatDateLabel = (yyyyMmDd: string, opts?: { withWeekday?: boolean; withYear?: boolean }): string => {
  const [y, m, d] = yyyyMmDd.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return yyyyMmDd;
  const weekday = opts?.withWeekday ? `(${DOW_LABELS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]})` : '';
  const year = opts?.withYear ? `${y}年` : '';
  return `${year}${m}月${d}日${weekday}`;
};

/** 姓名の半角スペース統一表示 (docs/design/v03-user-employee-refinement.md §5 氏名表示規約)。
 *  姓と(ミドル/名)の間は必ず半角スペース1個 (全角スペースは使わない)。直接文字列結合は禁止、
 *  必ずこの関数経由で表示すること (middle_name抜け防止、Sec L-2)。 */
const joinPersonName = (familyName: string | null | undefined, middleName: string | null | undefined, givenName: string | null | undefined): string =>
  [familyName, middleName, givenName].filter((s): s is string => !!s).join(' ');

/** ユーザーの表示名を返す。実氏名user (family_nameがある) はfamily+middle+given、
 *  それ以外 (役職名/組織名user) はdisplay_nameをそのまま返す (§2-4 display_name分離方針)。 */
export const formatUserName = (user: {
  display_name?: string | null;
  name?: string | null;
  family_name?: string | null;
  middle_name?: string | null;
  given_name?: string | null;
}): string => {
  if (user.family_name) return joinPersonName(user.family_name, user.middle_name, user.given_name);
  return user.display_name ?? user.name ?? '';
};

/** 社員/外部人材の表示名 (常に実氏名、家族名は必須項目のため必ず存在する)。 */
export const formatEmployeeName = (person: {
  family_name: string;
  middle_name?: string | null;
  given_name: string;
}): string => joinPersonName(person.family_name, person.middle_name, person.given_name);

/** 経過秒数を「N秒」「N分M秒」で表す (v0.4 F-2、シフト下書き生成の経過時間表示。
 *  pages/AiMenuChatPage.tsx のインラインヘルパーと同じ書式で、生成中インジケーターの表示規約を揃える)。 */
export const formatElapsedDuration = (sec: number): string =>
  (sec < 60 ? `${sec}秒` : `${Math.floor(sec / 60)}分${sec % 60}秒`);

/** ISO日時から現在までの経過時間を「たった今」「N分前」「N時間M分前」で表す (AI-OCR進捗表示用、2026-08-02) */
export const formatElapsedSince = (isoDateTime: string, now: Date = new Date()): string => {
  const diffMs = now.getTime() - new Date(isoDateTime).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'たった今';
  if (diffMin < 60) return `${diffMin}分前`;
  const hours = Math.floor(diffMin / 60);
  const mins = diffMin % 60;
  return `${hours}時間${mins}分前`;
};
